/**
 * Processamentos auxiliares offline, não destrutivos.
 * O arquivo original continua intacto; o resultado é um buffer derivado.
 */
import { clamp, dbToGain } from '../utils/misc.js';

/**
 * Noise gate com attack/release/hang, para exportação offline.
 * Não hard-muta: mantém um piso de ganho para preservar respirações.
 */
export function applyNoiseGate(
  input: Float32Array,
  sampleRate: number,
  opts: { thresholdDb?: number; attackMs?: number; releaseMs?: number; holdMs?: number; rangeDb?: number } = {}
): Float32Array {
  const threshold = dbToGain(opts.thresholdDb ?? -52);
  const attack = Math.max(0.5, opts.attackMs ?? 6) / 1000;
  const release = Math.max(10, opts.releaseMs ?? 220) / 1000;
  const holdSamples = Math.round(sampleRate * Math.max(0, opts.holdMs ?? 90) / 1000);
  const floor = dbToGain(opts.rangeDb ?? -28);
  const out = new Float32Array(input.length);
  const envAttack = Math.exp(-1 / (sampleRate * attack));
  const envRelease = Math.exp(-1 / (sampleRate * Math.max(0.01, release * 0.35)));
  const gainAttack = 1 - Math.exp(-1 / (sampleRate * attack));
  const gainRelease = 1 - Math.exp(-1 / (sampleRate * release));
  let env = 0;
  let gain = 1;
  let hold = 0;

  for (let i = 0; i < input.length; i++) {
    const a = Math.abs(input[i]);
    env = a > env ? a + (env - a) * envAttack : a + (env - a) * envRelease;
    if (env > threshold * 0.72) hold = holdSamples;
    else if (hold > 0) hold--;
    const target = env > threshold * 0.72 || hold > 0 ? 1 : floor;
    gain += (target - gain) * (target > gain ? gainAttack : gainRelease);
    out[i] = input[i] * gain;
  }
  return out;
}

/** High-pass por biquad (RBJ) para processamento offline. */
export function applyHighPass(input: Float32Array, sampleRate: number, freqHz: number, q = 0.707): Float32Array {
  const f0 = clamp(freqHz, 20, sampleRate * 0.45);
  const w0 = 2 * Math.PI * f0 / sampleRate;
  const cw = Math.cos(w0), sw = Math.sin(w0);
  const alpha = sw / (2 * q);
  const b0 = (1 + cw) / 2, b1 = -(1 + cw), b2 = (1 + cw) / 2;
  const a0 = 1 + alpha, a1 = -2 * cw, a2 = 1 - alpha;
  const B0 = b0 / a0, B1 = b1 / a0, B2 = b2 / a0, A1 = a1 / a0, A2 = a2 / a0;
  const out = new Float32Array(input.length);
  let x1=0,x2=0,y1=0,y2=0;
  for(let i=0;i<input.length;i++){
    const x0=input[i];
    const y0=B0*x0+B1*x1+B2*x2-A1*y1-A2*y2;
    out[i]=y0; x2=x1;x1=x0;y2=y1;y1=y0;
  }
  return out;
}

/** EQ de bandas com biquad RBJ. `bands` possui frequency/gainDb/Q/type. */
export function applyEq(
  input: Float32Array,
  sampleRate: number,
  bands: { freqHz: number; gainDb: number; q: number; type: BiquadFilterType }[]
): Float32Array {
  let out = Float32Array.from(input);
  for (const band of bands) {
    const A = Math.pow(10, clamp(band.gainDb, -18, 18) / 40);
    const f0 = clamp(band.freqHz, 20, sampleRate * 0.45);
    const Q = clamp(band.q, 0.1, 12);
    const w0 = 2 * Math.PI * f0 / sampleRate;
    const cw = Math.cos(w0), sw = Math.sin(w0);
    const alpha = sw / (2 * Q);
    let b0: number,b1: number,b2: number,a0: number,a1: number,a2: number;
    if (band.type === 'lowshelf') {
      const r = 2 * Math.sqrt(A) * alpha;
      b0=A*((A+1)-(A-1)*cw+r); b1=2*A*((A-1)-(A+1)*cw); b2=A*((A+1)-(A-1)*cw-r);
      a0=(A+1)+(A-1)*cw+r; a1=-2*((A-1)+(A+1)*cw); a2=(A+1)+(A-1)*cw-r;
    } else if (band.type === 'highshelf') {
      const r = 2 * Math.sqrt(A) * alpha;
      b0=A*((A+1)+(A-1)*cw+r); b1=-2*A*((A-1)+(A+1)*cw); b2=A*((A+1)+(A-1)*cw-r);
      a0=(A+1)-(A-1)*cw+r; a1=2*((A-1)-(A+1)*cw); a2=(A+1)-(A-1)*cw-r;
    } else {
      b0=1+alpha*A; b1=-2*cw; b2=1-alpha*A; a0=1+alpha/A; a1=-2*cw; a2=1-alpha/A;
    }
    const B0=b0/a0,B1=b1/a0,B2=b2/a0,A1=a1/a0,A2=a2/a0;
    const next=new Float32Array(out.length); let x1=0,x2=0,y1=0,y2=0;
    for(let i=0;i<out.length;i++){
      const x0=out[i]; const y0=B0*x0+B1*x1+B2*x2-A1*y1-A2*y2;
      next[i]=y0;x2=x1;x1=x0;y2=y1;y1=y0;
    }
    out=next;
  }
  return out;
}
